import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { getAccessToken } from "../../../storage/storage";
import { FormatDate } from "../../../utils/FormateDate";

const COLORS = {
  primary: "#586989",
  cta: "#FFF5CE",
  warnBg: "#fff8e1",
  warnBorder: "#f59e0b",
  warnText: "#92400e",
  warnBody: "#78350f",
};

const extractApiErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    // Common DRF / Django keys, in priority order.
    const direct = data.error || data.message || data.detail || data.non_field_errors;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    if (Array.isArray(direct) && direct.length > 0) {
      return String(direct[0]);
    }
    // DRF field-validation shape: { field_name: ["msg", ...], ... }
    for (const key of Object.keys(data)) {
      const v = data[key];
      if (typeof v === "string" && v.trim()) return v.trim();
      if (Array.isArray(v) && v.length > 0 && typeof v[0] === "string") {
        return v[0];
      }
    }
  }
  if (err?.message && typeof err.message === "string") return err.message;
  return fallback;
};

const cleanerDisplayName = (c) => {
  if (!c) return "";
  const firstLast = [c.first_name, c.surname || c.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (firstLast) return firstLast;
  return (
    c.name ||
    c.full_name ||
    c.cleaner_name ||
    (c.id != null ? `Cleaner #${c.id}` : "")
  );
};

export default function AssignCleanerModal({
  open,
  onClose,
  selectedStays,
  propertyId,
  propertyName,
  onAssigned,
}) {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [cleaners, setCleaners] = useState([]);
  const [cleanersLoading, setCleanersLoading] = useState(false);
  const [cleanersError, setCleanersError] = useState(null);
  const [selectedCleanerId, setSelectedCleanerId] = useState("");
  const [reassignAcknowledged, setReassignAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedCleanerId("");
    setReassignAcknowledged(false);
    setCleanersError(null);
  }, [open]);

  useEffect(() => {
    if (!open || !propertyId) return;
    setCleanersLoading(true);
    axios
      .get(`${BASE_URL}/api/accepted-cleaners/?property_id=${propertyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        const acceptedOnly = list.filter((c) => {
          const status = String(c?.status || c?.invitation_status || "")
            .toUpperCase();
          // Accept records that explicitly say ACCEPTED, or those without a
          // status field (endpoint is named accepted-cleaners — assume server
          // already filtered).
          return status === "" || status === "ACCEPTED";
        });
        setCleaners(acceptedOnly);
      })
      .catch((err) => {
        setCleanersError(
          extractApiErrorMessage(err, "Couldn't load cleaners. Please try again.")
        );
      })
      .finally(() => setCleanersLoading(false));
  }, [open, propertyId, BASE_URL, token]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  const classifyStay = (stay, newCleanerId) => {
    const existingId =
      stay?.cleaner_id ?? stay?.cleaner?.id ?? stay?.cleaner ?? null;
    const status = String(stay?.status || "").toUpperCase();
    const hasOtherCleaner =
      !!stay?.cleaner_name &&
      (existingId == null || String(existingId) !== String(newCleanerId));
    if (!hasOtherCleaner) return { kind: "unassigned" };
    if (status === "ACCEPTED") return { kind: "accepted" };
    if (status === "PENDING") return { kind: "pending" };
    return { kind: "other" };
  };

  const { pendingConflicts, acceptedConflicts, otherConflicts } = useMemo(() => {
    if (!selectedCleanerId)
      return { pendingConflicts: [], acceptedConflicts: [], otherConflicts: [] };
    const buckets = { pendingConflicts: [], acceptedConflicts: [], otherConflicts: [] };
    for (const s of selectedStays) {
      const { kind } = classifyStay(s, selectedCleanerId);
      if (kind === "pending") buckets.pendingConflicts.push(s);
      else if (kind === "accepted") buckets.acceptedConflicts.push(s);
      else if (kind === "other") buckets.otherConflicts.push(s);
    }
    return buckets;
  }, [selectedStays, selectedCleanerId]);

  const conflicts = useMemo(
    () => [...acceptedConflicts, ...pendingConflicts, ...otherConflicts],
    [acceptedConflicts, pendingConflicts, otherConflicts]
  );
  const conflictCount = conflicts.length;
  const acceptedCount = acceptedConflicts.length;
  const pendingCount = pendingConflicts.length;

  const existingCleanerNames = useMemo(
    () =>
      Array.from(
        new Set(conflicts.map((s) => s.cleaner_name).filter(Boolean))
      ),
    [conflicts]
  );

  const newCleanerName = cleanerDisplayName(
    cleaners.find(
      (c) => String(c.id ?? c.cleaner_id) === String(selectedCleanerId)
    )
  );

  const noEligibleCleaners =
    !cleanersLoading && !cleanersError && cleaners.length === 0;

  const canConfirm =
    !submitting &&
    !!selectedCleanerId &&
    !noEligibleCleaners &&
    (conflictCount === 0 || reassignAcknowledged);

  const warningSeverity = acceptedCount > 0 ? "high" : "medium";

  const warningHeadline = (() => {
    if (acceptedCount > 0 && pendingCount > 0) {
      return `${conflictCount} stay${
        conflictCount === 1 ? "" : "s"
      } already assigned (${acceptedCount} accepted, ${pendingCount} pending)`;
    }
    if (acceptedCount > 0) {
      return `${acceptedCount} stay${
        acceptedCount === 1 ? "" : "s"
      } already accepted by ${
        existingCleanerNames.length === 1
          ? existingCleanerNames[0]
          : "another cleaner"
      }`;
    }
    return `${pendingCount || conflictCount} stay${
      (pendingCount || conflictCount) === 1 ? "" : "s"
    } already assigned to ${
      existingCleanerNames.length === 1
        ? existingCleanerNames[0]
        : "another cleaner"
    }`;
  })();

  // Group conflicts by current cleaner_name, with per-stay status so we can
  // surface accepted stays inline as red-tinted markers next to the ID.
  const conflictGroups = useMemo(() => {
    if (!selectedCleanerId) return [];
    const map = new Map();
    for (const s of conflicts) {
      const name = s.cleaner_name || "another cleaner";
      if (!map.has(name)) {
        map.set(name, { cleanerName: name, items: [] });
      }
      const { kind } = classifyStay(s, selectedCleanerId);
      map.get(name).items.push({ id: s.id, kind });
    }
    return Array.from(map.values());
  }, [conflicts, selectedCleanerId]);

  const acknowledgeLabel = (() => {
    if (acceptedCount > 0 && pendingCount > 0) {
      return `I understand this will change the cleaner for ${conflictCount} stays (${acceptedCount} already accepted)`;
    }
    if (acceptedCount > 0) {
      return `I understand this will unassign ${
        acceptedCount === 1 ? "a stay that was" : `${acceptedCount} stays that were`
      } already accepted`;
    }
    return `I understand this will change the cleaner for ${pendingCount || conflictCount} stay${
      (pendingCount || conflictCount) === 1 ? "" : "s"
    }`;
  })();

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/api/stays/bulk-assign/`,
        {
          cleaner_id: parseInt(selectedCleanerId, 10),
          stay_ids: selectedStays.map((s) => s.id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      const knownStatuses = new Set(["assigned", "conflict"]);
      const otherFailed = results.filter(
        (r) => r?.status && !knownStatuses.has(String(r.status).toLowerCase())
      );

      const assignedCount =
        data.assigned_count ??
        results.filter(
          (r) => String(r?.status).toLowerCase() === "assigned"
        ).length;
      const conflictCountFromApi =
        data.conflict_count ??
        results.filter(
          (r) => String(r?.status).toLowerCase() === "conflict"
        ).length;

      const cleanerLabel = newCleanerName || "the cleaner";
      const totalProcessed = assignedCount + conflictCountFromApi;

      if (totalProcessed > 0) {
        const parts = [];
        if (assignedCount > 0) {
          parts.push(
            `${assignedCount} assigned to ${cleanerLabel}`
          );
        }
        if (conflictCountFromApi > 0) {
          parts.push(
            `${conflictCountFromApi} reassigned`
          );
        }
        toast.success(parts.join(" · "));
      }

      if (otherFailed.length > 0) {
        const firstMessage =
          otherFailed[0]?.message ||
          `${otherFailed.length} stay${
            otherFailed.length === 1 ? "" : "s"
          } could not be processed`;
        toast.error(firstMessage);
      } else if (totalProcessed === 0) {
        toast.error("No stays were assigned. Please try again.");
      }

      onAssigned?.(data);
      onClose();
    } catch (err) {
      toast.error(
        extractApiErrorMessage(err, "Couldn't assign cleaner. Please try again."),
        { duration: 6000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Assign cleaner to ${selectedStays.length} stays`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2
              className="text-base font-bold"
              style={{ color: COLORS.primary }}
            >
              Assign cleaner to {selectedStays.length} stay
              {selectedStays.length === 1 ? "" : "s"}
            </h2>
            {propertyName && (
              <p className="text-xs text-gray-500 mt-0.5">{propertyName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <FaTimes />
          </button>
        </div>

        <div className="mt-4">
          <label
            className="block text-xs font-semibold mb-1"
            style={{ color: COLORS.primary }}
          >
            Select cleaner
          </label>
          {cleanersLoading ? (
            <div className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500">
              Loading cleaners…
            </div>
          ) : cleanersError ? (
            <div className="border border-red-300 bg-red-50 rounded-md px-3 py-2 text-sm text-red-700">
              {cleanersError}
            </div>
          ) : noEligibleCleaners ? (
            <div className="border border-amber-300 bg-amber-50 rounded-md px-3 py-2 text-sm text-amber-800">
              No cleaners have accepted an invitation for this property.
              Invite a cleaner first.
            </div>
          ) : (
            <select
              value={selectedCleanerId}
              onChange={(e) => {
                setSelectedCleanerId(e.target.value);
                setReassignAcknowledged(false);
              }}
              disabled={submitting}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#586989]"
            >
              <option value="">— Choose a cleaner —</option>
              {cleaners.map((c) => {
                const id = c.id ?? c.cleaner_id;
                return (
                  <option key={id} value={id}>
                    {cleanerDisplayName(c)}
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {conflictCount > 0 && (
          <>
            <div
              className="mt-4 rounded-md border-2 px-3 py-2"
              style={
                warningSeverity === "high"
                  ? {
                      background: "#fef2f2",
                      borderColor: "#dc2626",
                    }
                  : {
                      background: COLORS.warnBg,
                      borderColor: COLORS.warnBorder,
                    }
              }
            >
              <div
                className="flex items-center gap-2 text-xs font-bold"
                style={{
                  color: warningSeverity === "high" ? "#991b1b" : COLORS.warnText,
                }}
              >
                <FaExclamationTriangle />
                {warningHeadline}
              </div>
              <ul
                className="text-xs mt-2 leading-relaxed space-y-1 list-disc pl-4"
                style={{
                  color: warningSeverity === "high" ? "#7f1d1d" : COLORS.warnBody,
                }}
              >
                {conflictGroups.map((group) => {
                  const ids = group.items;
                  const plural = ids.length !== 1;
                  return (
                    <li key={group.cleanerName}>
                      Stay{plural ? "s" : ""}{" "}
                      {ids.map((it, i) => (
                        <span key={it.id}>
                          <span className="font-semibold">#{it.id}</span>
                          {it.kind === "accepted" && (
                            <span
                              className="ml-0.5 font-semibold"
                              style={{ color: "#991b1b" }}
                            >
                              {" "}
                              (accepted)
                            </span>
                          )}
                          {i < ids.length - 2
                            ? ", "
                            : i === ids.length - 2
                            ? " and "
                            : ""}
                        </span>
                      ))}{" "}
                      {plural ? "are" : "is"} assigned to{" "}
                      <span className="font-semibold">{group.cleanerName}</span>
                      .
                    </li>
                  );
                })}
              </ul>
              <p
                className="text-xs mt-2 leading-relaxed"
                style={{
                  color: warningSeverity === "high" ? "#7f1d1d" : COLORS.warnBody,
                }}
              >
                Confirming will unassign{" "}
                <span className="font-semibold">
                  {existingCleanerNames.length === 1
                    ? existingCleanerNames[0]
                    : "the current cleaners"}
                </span>{" "}
                and assign{" "}
                <span className="font-semibold">
                  {newCleanerName || "the new cleaner"}
                </span>{" "}
                to those stays.
              </p>
            </div>

            <label className="flex items-start gap-2 mt-3 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={reassignAcknowledged}
                onChange={(e) => setReassignAcknowledged(e.target.checked)}
                className="mt-0.5 accent-[#586989]"
                disabled={submitting}
              />
              <span>{acknowledgeLabel}</span>
            </label>
          </>
        )}

        <div className="mt-4">
          <div
            className="text-xs font-semibold mb-1"
            style={{ color: COLORS.primary }}
          >
            Selected stays
          </div>
          <div className="border border-gray-200 bg-gray-50 rounded-md max-h-40 overflow-y-auto">
            {selectedStays.map((s) => {
              const kind = selectedCleanerId
                ? classifyStay(s, selectedCleanerId).kind
                : "neutral";
              let tag = null;
              if (kind === "accepted") {
                tag = {
                  text: `Accepted by ${s.cleaner_name} — will be reassigned`,
                  bg: "#fee2e2",
                  color: "#991b1b",
                };
              } else if (kind === "pending") {
                tag = {
                  text: `Pending with ${s.cleaner_name} — not responded`,
                  bg: "#fef3c7",
                  color: "#92400e",
                };
              } else if (kind === "other") {
                tag = {
                  text: `Reassigning from ${s.cleaner_name}`,
                  bg: "#fef3c7",
                  color: "#92400e",
                };
              } else if (kind === "unassigned" && selectedCleanerId) {
                tag = {
                  text: "Unassigned",
                  bg: "#e5e7eb",
                  color: "#374151",
                };
              }

              return (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-100 last:border-b-0 text-xs"
                >
                  <span
                    className="font-semibold flex-shrink-0"
                    style={{ color: COLORS.primary }}
                  >
                    #{s.id}
                  </span>
                  <span className="text-gray-500 truncate flex-1">
                    {s.lead_guest_name && (
                      <span className="text-gray-700 font-medium">
                        {s.lead_guest_name} ·{" "}
                      </span>
                    )}
                    {FormatDate(s.check_in_date)} – {FormatDate(s.check_out_date)}
                    {s.number_of_guests != null
                      ? ` · ${s.number_of_guests} guest${s.number_of_guests !== 1 ? "s" : ""}`
                      : ""}
                    {s.number_of_pets > 0
                      ? ` · ${s.number_of_pets} pet${s.number_of_pets !== 1 ? "s" : ""}`
                      : ""}
                  </span>
                  {tag && (
                    <span
                      className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium"
                      style={{ background: tag.bg, color: tag.color }}
                    >
                      {tag.text}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-md border text-sm disabled:opacity-50"
            style={{ color: COLORS.primary, borderColor: COLORS.primary }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canConfirm}
            className="flex-1 px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50"
            style={{ background: COLORS.primary, color: "white" }}
          >
            {submitting ? "Assigning…" : "Confirm assignment"}
          </button>
        </div>
      </div>
    </div>
  );
}
